import { createSelector, MemoizedSelector } from '@ngrx/store';
import { getAdapterSelector } from 'src/app/ngrx-helpers/adapters/utils';
import { VideoStatus } from '../enums/video-status.enum';
import { VideoErrorDescriptions, VideoErrorType } from '../video-errors';
import { VideoState } from './reducer';
import { DeviceWarningState } from '../enums/device-warning-state.enum';
import { MediaDevice } from 'src/app/shared/types/media-device.type';
import { capitalize } from 'lodash-es';

const warningTooltips: Record<
  DeviceWarningState,
  (device: MediaDevice, active: boolean) => string
> = {
  [DeviceWarningState.None]: (device, active) => `Turn ${device} ${active ? 'off' : 'on'} `,
  [DeviceWarningState.NotAvailable]: (device) => `Can't connect to a ${device}`,
  [DeviceWarningState.Blocked]: (device) => `${capitalize(device)} is blocked`,
  [DeviceWarningState.InUse]: (device) => `${capitalize(device)} is in use by another application`,
  [DeviceWarningState.Other]: (device, active) =>
    `Could not turn your ${device} ${active ? 'off' : 'on'} `,
};

export function createVideoSelectors(
  stateKeyOrSelector: string | MemoizedSelector<object, VideoState>,
) {
  const selectVideoState = getAdapterSelector<VideoState>(stateKeyOrSelector);

  const selectIsActive = createSelector(selectVideoState, ({ active }) => active);

  const selectIsInitialized = createSelector(selectVideoState, ({ initialized }) => initialized);

  const selectStatus = createSelector(selectVideoState, ({ status }) => status);

  const selectIsConnecting = createSelector(
    selectStatus,
    (status) => status === VideoStatus.Connecting,
  );

  const selectIsConnected = createSelector(
    selectStatus,
    (status) => status === VideoStatus.Connected,
  );

  const selectIsPlaying = createSelector(selectStatus, (status) => status === VideoStatus.Playing);

  const selectConnectionProgressPercentage = createSelector(
    selectVideoState,
    ({ connectionProgressPercentage }) => connectionProgressPercentage,
  );

  const selectPlayCount = createSelector(selectVideoState, ({ playCount }) => playCount);

  const selectCameraActive = createSelector(selectVideoState, ({ cameraActive }) => cameraActive);

  const selectMicrophoneActive = createSelector(
    selectVideoState,
    ({ microphoneActive }) => microphoneActive,
  );

  const selectRetainedCameraActive = createSelector(
    selectVideoState,
    ({ retainedCameraActive }) => retainedCameraActive,
  );

  const selectRetainedMicrophoneActive = createSelector(
    selectVideoState,
    ({ retainedMicrophoneActive }) => retainedMicrophoneActive,
  );

  const selectCameraWarningState = createSelector(
    selectVideoState,
    ({ cameraWarningState }) => cameraWarningState,
  );

  const selectCameraNotAvailable = createSelector(
    selectCameraWarningState,
    (warningState) => warningState === DeviceWarningState.NotAvailable,
  );

  const selectCameraWarningTooltip = createSelector(
    selectCameraActive,
    selectCameraWarningState,
    (active, warningState) => warningTooltips[warningState]('camera', active),
  );

  const selectMicrophoneWarningState = createSelector(
    selectVideoState,
    ({ microphoneWarningState }) => microphoneWarningState,
  );

  const selectMicrophoneNotAvailable = createSelector(
    selectMicrophoneWarningState,
    (warningState) => warningState === DeviceWarningState.NotAvailable,
  );

  const selectMicrophoneWarningTooltip = createSelector(
    selectMicrophoneActive,
    selectMicrophoneWarningState,
    (active, warningState) => warningTooltips[warningState]('microphone', active),
  );

  const selectMicrophoneAndCameraActive = createSelector(
    selectMicrophoneActive,
    selectCameraActive,
    (microphoneActive, cameraActive) => microphoneActive && cameraActive,
  );

  const selectMicrophoneOrCameraNotAvailable = createSelector(
    selectCameraWarningState,
    selectMicrophoneWarningState,
    (cameraWarningState, microphoneWarningState) =>
      cameraWarningState === DeviceWarningState.NotAvailable ||
      microphoneWarningState === DeviceWarningState.NotAvailable,
  );

  const selectError = createSelector(selectVideoState, (state) => state.error);

  const selectErrorDescription = createSelector(
    selectError,
    (error) => error && VideoErrorDescriptions[error.type],
  );

  const selectErrorRequiresReconnect = createSelector(selectError, (error) =>
    [
      VideoErrorType.InitializeServiceError,
      VideoErrorType.ConnectError,
      VideoErrorType.SpeakScriptError,
      VideoErrorType.ServerDisconnectedError,
    ].includes(error?.type),
  );

  const selectErrorRequiresPlay = createSelector(
    selectError,
    (error) => VideoErrorType.AutoplayError === error?.type,
  );

  const selectIsStable = createSelector(
    selectIsPlaying,
    selectError,
    (isPlaying, error) => isPlaying && !error,
  );

  const selectShouldDisplayMediaRequestPopover = createSelector(
    selectIsStable,
    selectPlayCount,
    (isStable, playCount) => isStable && playCount === 1, // only show the first time the video is played
  );

  return {
    selectVideoState,
    selectIsActive,
    selectIsInitialized,
    selectStatus,
    selectIsConnecting,
    selectIsConnected,
    selectIsPlaying,
    selectConnectionProgressPercentage,
    selectPlayCount,
    selectCameraActive,
    selectMicrophoneActive,
    selectRetainedCameraActive,
    selectRetainedMicrophoneActive,
    selectCameraWarningState,
    selectCameraNotAvailable,
    selectCameraWarningTooltip,
    selectMicrophoneWarningState,
    selectMicrophoneNotAvailable,
    selectMicrophoneWarningTooltip,
    selectMicrophoneAndCameraActive,
    selectMicrophoneOrCameraNotAvailable,
    selectError,
    selectErrorDescription,
    selectErrorRequiresReconnect,
    selectErrorRequiresPlay,
    selectIsStable,
    selectShouldDisplayMediaRequestPopover,
  };
}

export type VideoSelectors = ReturnType<typeof createVideoSelectors>;
